import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import TIMEZONES from '@shared/timezones';

interface FormatEventDateTimeV2Options {
  startsAt: string;
  endsAt?: string;
  showStartTime?: boolean;
  showEndTime?: boolean;
  timeZone?: string;
  timeFormat?: '12_hour' | '24_hour' | 'military';
  long?: boolean;
}

/**
 * Converts Rails timezone name to IANA timezone identifier
 * @param railsTimezone - Rails timezone name (e.g., "Eastern Time (US & Canada)")
 * @returns IANA timezone identifier (e.g., "America/New_York")
 */
function convertRailsToIANATimezone(railsTimezone: string): string {
  // If it's already an IANA timezone (contains /), return as is
  if (railsTimezone && railsTimezone.includes('/')) {
    return railsTimezone;
  }
  
  // Find matching timezone in our list
  const timezone = TIMEZONES.find(tz => tz.label === railsTimezone);
  
  // Return the IANA value if found, otherwise default to UTC
  return timezone?.value || 'UTC';
}

/**
 * Formats event date/time for V2 events
 * Provides consistent formatting across all V2 components
 */
export function formatEventDateTimeV2({
  startsAt,
  endsAt,
  showStartTime = true,
  showEndTime = true,
  timeZone = 'America/New_York',
  timeFormat = '12_hour',
  long = true
}: FormatEventDateTimeV2Options): string {
  if (!startsAt) return '';

  try {
    // Convert Rails timezone to IANA if needed
    const ianaTimeZone = convertRailsToIANATimezone(timeZone);
    const startDate = parseISO(startsAt);
    const endDate = endsAt ? parseISO(endsAt) : startDate;

    // Check if dates are valid
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return '';
    }

  // If not showing any times, just show date range
  if (!showStartTime && !showEndTime) {
    return formatDateRange(startDate, endDate, ianaTimeZone, long);
  }

  // Check if same day in the event's timezone
  const isSameDay = formatInTimeZone(startDate, ianaTimeZone, 'yyyy-MM-dd') === formatInTimeZone(endDate, ianaTimeZone, 'yyyy-MM-dd');

  // If start and end are exactly the same time in the event's timezone
  if (formatInTimeZone(startDate, ianaTimeZone, 'yyyy-MM-dd HH:mm') === formatInTimeZone(endDate, ianaTimeZone, 'yyyy-MM-dd HH:mm')) {
    return formatDayWithTime(startDate, ianaTimeZone, timeFormat, long, showStartTime || showEndTime);
  }

  // Same day, different times
  if (isSameDay) {
    return formatDayWithTimeRange(startDate, endDate, ianaTimeZone, timeFormat, long, showStartTime, showEndTime);
  }

  // Different days
  return formatDaysWithTimeRange(startDate, endDate, ianaTimeZone, timeFormat, long, showStartTime, showEndTime);
  } catch (error) {
    return '';
  }
}

function formatDateRange(startDate: Date, endDate: Date, timeZone: string, long: boolean): string {
  const dateFormat = long ? 'MMMM d, yyyy' : 'MMM d, yyyy';
  
  const startStr = formatInTimeZone(startDate, timeZone, dateFormat);
  const endStr = formatInTimeZone(endDate, timeZone, dateFormat);

  if (startStr === endStr) {
    return startStr;
  }

  // Check if same month in the event's timezone
  if (formatInTimeZone(startDate, timeZone, 'yyyy-MM') === formatInTimeZone(endDate, timeZone, 'yyyy-MM')) {
    return long 
      ? `${formatInTimeZone(startDate, timeZone, 'MMMM d')}-${formatInTimeZone(endDate, timeZone, 'd, yyyy')}`
      : `${formatInTimeZone(startDate, timeZone, 'MMM d')}-${formatInTimeZone(endDate, timeZone, 'd, yyyy')}`;
  }

  // Different months
  return `${startStr} - ${endStr}`;
}

function formatDayWithTime(
  date: Date,
  timeZone: string,
  timeFormat: string,
  long: boolean,
  showTime: boolean
): string {
  const dateFormat = long ? 'MMMM d, yyyy' : 'MMM d, yyyy';
  let result = formatInTimeZone(date, timeZone, dateFormat);

  if (showTime) {
    const timeStr = formatTime(date, timeZone, timeFormat);
    result += ` at ${timeStr}`;
  }

  return result;
}

function formatDayWithTimeRange(
  startDate: Date,
  endDate: Date,
  timeZone: string,
  timeFormat: string,
  long: boolean,
  showStartTime: boolean,
  showEndTime: boolean
): string {
  const dateFormat = long ? 'MMMM d, yyyy' : 'MMM d, yyyy';
  let result = formatInTimeZone(startDate, timeZone, dateFormat);

  if (timeFormat === '12_hour') {
    const startMeridian = formatInTimeZone(startDate, timeZone, 'a');
    const endMeridian = formatInTimeZone(endDate, timeZone, 'a');

    if (startMeridian === endMeridian) {
      // Same AM/PM - "July 5, 2025, 12:00 - 1:00 PM"
      if (showStartTime && showEndTime) {
        result += `, ${formatInTimeZone(startDate, timeZone, 'h:mm')} - ${formatInTimeZone(endDate, timeZone, 'h:mm a')}`;
      } else if (showStartTime) {
        result += ` at ${formatInTimeZone(startDate, timeZone, 'h:mm a')}`;
      } else if (showEndTime) {
        result += ` until ${formatInTimeZone(endDate, timeZone, 'h:mm a')}`;
      }
    } else {
      // Different AM/PM - "July 5, 2025, 11:00 AM - 1:00 PM"
      if (showStartTime && showEndTime) {
        result += `, ${formatInTimeZone(startDate, timeZone, 'h:mm a')} - ${formatInTimeZone(endDate, timeZone, 'h:mm a')}`;
      } else if (showStartTime) {
        result += ` at ${formatInTimeZone(startDate, timeZone, 'h:mm a')}`;
      } else if (showEndTime) {
        result += ` until ${formatInTimeZone(endDate, timeZone, 'h:mm a')}`;
      }
    }
  } else {
    // 24-hour format
    if (showStartTime && showEndTime) {
      result += `, ${formatInTimeZone(startDate, timeZone, 'HH:mm')} - ${formatInTimeZone(endDate, timeZone, 'HH:mm')}`;
    } else if (showStartTime) {
      result += ` at ${formatInTimeZone(startDate, timeZone, 'HH:mm')}`;
    } else if (showEndTime) {
      result += ` until ${formatInTimeZone(endDate, timeZone, 'HH:mm')}`;
    }
  }

  return result;
}

function formatDaysWithTimeRange(
  startDate: Date,
  endDate: Date,
  timeZone: string,
  timeFormat: string,
  long: boolean,
  showStartTime: boolean,
  showEndTime: boolean
): string {
  const dateFormat = long ? 'MMMM d, yyyy' : 'MMM d, yyyy';
  const timeFormatStr = timeFormat === '12_hour' ? 'h:mm a' : 'HH:mm';

  let startStr = formatInTimeZone(startDate, timeZone, dateFormat);
  let endStr = formatInTimeZone(endDate, timeZone, dateFormat);

  if (showStartTime) {
    startStr += ` at ${formatInTimeZone(startDate, timeZone, timeFormatStr)}`;
  }

  if (showEndTime) {
    endStr += ` at ${formatInTimeZone(endDate, timeZone, timeFormatStr)}`;
  }

  return `${startStr} - ${endStr}`;
}

function formatTime(date: Date, timeZone: string, timeFormat: string): string {
  if (timeFormat === '12_hour') {
    return formatInTimeZone(date, timeZone, 'h:mm a');
  } else {
    return formatInTimeZone(date, timeZone, 'HH:mm');
  }
}

// Additional V2-specific formatters that match existing patterns

export function formatDateRangeSimple(startsAt: string, endsAt?: string, timeZone: string = 'America/New_York'): string {
  if (!startsAt) return '';
  
  // Convert Rails timezone to IANA if needed
  const ianaTimeZone = convertRailsToIANATimezone(timeZone);
  
  const startDate = parseISO(startsAt);
  const endDate = endsAt ? parseISO(endsAt) : startDate;
  
  if (formatInTimeZone(startDate, ianaTimeZone, 'yyyy-MM-dd') === formatInTimeZone(endDate, ianaTimeZone, 'yyyy-MM-dd')) {
    return formatInTimeZone(startDate, ianaTimeZone, 'MMM d, yyyy');
  }
  
  if (formatInTimeZone(startDate, ianaTimeZone, 'yyyy-MM') === formatInTimeZone(endDate, ianaTimeZone, 'yyyy-MM')) {
    return `${formatInTimeZone(startDate, ianaTimeZone, 'MMM d')}-${formatInTimeZone(endDate, ianaTimeZone, 'd, yyyy')}`;
  }
  
  return `${formatInTimeZone(startDate, ianaTimeZone, 'MMM d')} - ${formatInTimeZone(endDate, ianaTimeZone, 'MMM d, yyyy')}`;
}

export function formatTimeSimple(dateTime: string, timeZone?: string): string {
  if (!dateTime) return '';
  
  const date = parseISO(dateTime);
  if (timeZone) {
    // Convert Rails timezone to IANA if needed
    const ianaTimeZone = convertRailsToIANATimezone(timeZone);
    return formatInTimeZone(date, ianaTimeZone, 'h:mm a');
  }
  return format(date, 'h:mm a');
}

export function formatDateTimeSimple(dateTime: string, timeZone?: string): string {
  if (!dateTime) return '';
  
  const date = parseISO(dateTime);
  return timeZone
    ? formatInTimeZone(date, timeZone, 'MMM d, yyyy h:mm a')
    : format(date, 'MMM d, yyyy h:mm a');
}